import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Button, Card, Row, Col, Alert } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import ISO6391 from "iso-639-1";
import { Upload, X, Image as ImageIcon, Eye } from "lucide-react";
import { BiLaptop, BiMobile } from "react-icons/bi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumb from "../../components/Common/Bredcrump";
import StreamingContentRenderer from "../../components/Common/StreamingContentRenderer";
import { generateContentWithStreaming, mockGenerateContent, saveFreeFormContentDraft } from "../../services/freeFormContentService";
import useStreamingContent from "../../hooks/useStreamingContent";
import { uploadCompanyKnowledgeFiles } from "../../utils/Product";
import { useCompany } from "../../context/CompanyContext";
import "./FreeFormContent.css";
import axiosInstance from "../../utils/api";

const CreateFreeFormContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialFormData = location.state || {};
  const { companyId, companyKnowledgeDocs } = useCompany();

  const [formData, setFormData] = useState({
    title: initialFormData.title || "",
    prompt: initialFormData.prompt || "",
    language: initialFormData.language ? {
      label: initialFormData.language,
      value: initialFormData.language,
    } : null,
    locationFocus: initialFormData.locationFocus ? {
      label: initialFormData.locationFocus,
      value: initialFormData.locationFocus,
    } : null,
    role: initialFormData.role || "",
    goal: initialFormData.goal || "",
    constraints: initialFormData.constraints || "",
  });

  const [uploadedData, setUploadedData] = useState({
    tags: initialFormData.tags || "",
    uid: initialFormData.uid || "",
  });

  // Image upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(initialFormData.image || "");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const fileInputRef = useRef(null);

  const [allLocations, setAllLocations] = useState([]);

  // Content state tracking
  const [hasGeneratedContent, setHasGeneratedContent] = useState(false);
  const [savedContentId, setSavedContentId] = useState(null);
  const [isContentSaved, setIsContentSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Translation state
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState("");
  const [translationStream, setTranslationStream] = useState("");
  const [originalContent, setOriginalContent] = useState(null);

  // Preview state
  const [viewMode, setViewMode] = useState(null);

  // Metafields state
  const [metafields, setMetafields] = useState([]);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [initialContent, setInitialContent] = useState("");

  // Load location options for AsyncSelect
  const loadLocationOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback([]);
      return;
    }
    const filteredLocations = allLocations.filter(location =>
      location.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filteredLocations);
  };

  // Use the streaming content hook - MUST be called before any useEffect that uses its variables
  const {
    isGenerating,
    isStreaming,
    hasFirstChunk,
    isStreamingDone,
    streamingContent,
    finalContent,
    parsedHTMLContent,
    error,
    generateContent,
    resetContent,
    clearError
  } = useStreamingContent(generateContentWithStreaming);

  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/listing?type=country');
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const locationOptions = response.data.data.map(item => ({
            value: item.name,
            label: item.name,
          }));
          setAllLocations(locationOptions);
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    };
    fetchLocations();
  }, []);

  // Monitor content generation completion - NOW this is safe to use
  useEffect(() => {
    if (isStreamingDone && finalContent) {
      setHasGeneratedContent(true);
    }
  }, [isStreamingDone, finalContent]);

  // Monitor form changes after saving
  useEffect(() => {
    if (isContentSaved) {
      // Check if any form field has changed from the saved state
      const currentFormData = {
        title: formData.title,
        prompt: formData.prompt,
        language: formData.language?.value || formData.language,
        locationFocus: formData.locationFocus?.value || formData.locationFocus,
        role: formData.role,
        goal: formData.goal,
        constraints: formData.constraints,
        image: imageUrl
      };
      
      // This is a simplified check - in a real app you'd compare with the actual saved data
      setHasUnsavedChanges(true);
    }
  }, [formData, imageUrl, isContentSaved]);

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && isContentSaved) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isContentSaved]);

  // Language options using ISO6391
  const languageOptions = React.useMemo(
    () =>
      ISO6391.getAllNames().map((name) => ({
        value: ISO6391.getCode(name),
        label: name,
      })),
    []
  );

  // Image upload functions
  const validateImage = (file) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      setImageError("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return false;
    }

    if (file.size > maxSize) {
      setImageError("Image size should not exceed 10MB");
      return false;
    }

    setImageError("");
    return true;
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!validateImage(file)) return;

    setSelectedImage(file);
    setImageError("");

    // Upload the image immediately
    await uploadImage(file);
  };

  const uploadImage = async (file) => {
    if (!file) return;

    setIsUploadingImage(true);
    setImageError("");

    try {
      const formData = new FormData();
      formData.append("file[]", file);
      
      // Get the current company ID dynamically
      const currentCompanyId = await getCurrentCompanyId();
      if (!currentCompanyId) {
        setImageError("Could not determine company ID. Please try again.");
        return;
      }
      
      const jsonPayload = {
        request_type: "blog",
        additional_data: {
          documents: [currentCompanyId],
          uid: Date.now().toString() + "-" + Math.random().toString(36).substring(2, 6),
        },
      };
      formData.append("request_json", JSON.stringify(jsonPayload));

      // Upload the file first
      const response = await uploadCompanyKnowledgeFiles(formData);
      
      if (response.files && response.files.length > 0) {
        // The upload response might contain temporary URLs, so we need to get the permanent URL from knowledge base
        // Wait a moment for the backend to process and save to knowledge base
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Fetch the permanent URL from the company's knowledge base
        const permanentUrl = await getPermanentImageUrlFromKnowledgeBase(currentCompanyId, file.name);
        
        if (permanentUrl) {
          setImageUrl(permanentUrl);
        } else {
          // Fallback to the response URL if we can't get the permanent one
          setImageUrl(response.files[0]);
        }
      }
    } catch (error) {
      console.error('Image upload error:', error);
      setImageError(`Upload failed: ${error.message || 'Unknown error occurred'}`);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const getCurrentCompanyId = async () => {
    // Use companyId from context instead of making API call
    return companyId || null;
  };

  const getPermanentImageUrlFromKnowledgeBase = async (companyId, fileName) => {
    try {
      // Use companyKnowledgeDocs from context instead of making API call
      if (companyKnowledgeDocs && Array.isArray(companyKnowledgeDocs)) {
        const sortedDocs = companyKnowledgeDocs
          .filter(doc => {
            const contentType = doc.content_type || (doc.url && doc.url.match(/\.(jpg|jpeg|png|gif|webp)/i) ? 'image/jpeg' : null);
            return contentType && contentType.startsWith('image/');
          })
          .sort((a, b) => {
            const dateA = a.uploaded_at ? new Date(a.uploaded_at) : new Date(0);
            const dateB = b.uploaded_at ? new Date(b.uploaded_at) : new Date(0);
            return dateB - dateA;
          });
        
        if (sortedDocs.length > 0) {
          return sortedDocs[0].url || sortedDocs[0];
        }
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get permanent image URL from knowledge base:', error);
      return null;
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImageUrl("");
    setImageError("");
    setIsUploadingImage(false); // Stop any upload indication
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleReplaceImage = () => {
    // Clear current image and trigger file input
    removeImage();
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  };

  const handlePreview = async (mode) => {
    const currentId = savedContentId;
    if (!currentId) {
      toast.warning("Please save the content as draft before previewing.");
      return;
    }

    // Get the current content - prioritize the most complete content available
    let currentContent = "";
    if (translatedContent) {
      currentContent = translatedContent;
    } else if (finalContent) {
      currentContent = finalContent;
    } else if (parsedHTMLContent) {
      currentContent = parsedHTMLContent;
    } else if (streamingContent) {
      currentContent = streamingContent;
    }


    // Prepare content data for preview
    const previewData = {
      id: currentId,
      name: formData.title,
      title: formData.title,
      prompt: formData.prompt,
      language: formData.language?.label || "English",
      locationFocus: formData.locationFocus?.label || "India",
      role: formData.role,
      goal: formData.goal,
      constraints: formData.constraints,
      generatedContent: currentContent,
      generated_content: currentContent, // Also add the alternative field name
      image: imageUrl,
      status: isContentSaved ? "draft" : "unsaved",
      created_at: new Date().toISOString(),
      metafields: [],
      tags: uploadedData?.tags || "",
      uid: uploadedData?.uid || ""
    };


    try {
      navigate(`/skills/free-form-content/preview/${currentId}`, { 
        state: { 
          contentData: previewData,
          viewMode: mode || viewMode,
          source: 'create' // Track that we came from create mode
        } 
      });
    } catch (err) {
      toast.error("Failed to open preview.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate mandatory fields
    if (!formData.language || !formData.language.value) {
      toast.error("Please select a language. Language is required.");
      return;
    }
    
    if (!formData.locationFocus || !formData.locationFocus.value) {
      toast.error("Please select a location focus. Location is required.");
      return;
    }
    
    // Create comprehensive prompt by appending role, goal, and constraints
    let comprehensivePrompt = formData.prompt;
    
    if (formData.role || formData.goal || formData.constraints) {
      const additionalContext = [];
      if (formData.role) additionalContext.push(`Role: ${formData.role}`);
      if (formData.goal) additionalContext.push(`Goal: ${formData.goal}`);
      if (formData.constraints) additionalContext.push(`Constraints: ${formData.constraints}`);
      
      if (additionalContext.length > 0) {
        comprehensivePrompt = `${formData.prompt}\n\nAdditional Context:\n${additionalContext.join('\n')}`;
      }
    }
    
    // Prepare form data
    const requestData = {
      title: formData.title,
      prompt: comprehensivePrompt,
      language: formData.language?.value || formData.language,
      locationFocus: formData.locationFocus?.value || formData.locationFocus,
      role: formData.role,
      goal: formData.goal,
      constraints: formData.constraints,
      tags: uploadedData.tags,
      uid: uploadedData.uid,
      image: imageUrl // Include the image URL in the payload
    };

    // Reset content state when starting new generation
    setHasGeneratedContent(false);
    setIsContentSaved(false);
    setSavedContentId(null);
    setHasUnsavedChanges(false);
    
    // Reset translation state when generating new content
    setTranslatedContent("");
    setTranslationStream("");
    setOriginalContent("");
    setIsTranslating(false);
    
    // Reset edit mode state
    setInitialContent("");
    setEditedContent("");
    setIsEditMode(false);

    // Use the streaming hook to generate content
    await generateContent(requestData);
  };

  const handleBack = () => {
    navigate('/skills/free-form-content');
  };

  const handleSaveAsDraft = async () => {
    try {
      // Create comprehensive prompt by appending role, goal, and constraints
      let comprehensivePrompt = formData.prompt;
      
      if (formData.role || formData.goal || formData.constraints) {
        const additionalContext = [];
        if (formData.role) additionalContext.push(`Role: ${formData.role}`);
        if (formData.goal) additionalContext.push(`Goal: ${formData.goal}`);
        if (formData.constraints) additionalContext.push(`Constraints: ${formData.constraints}`);
        
        if (additionalContext.length > 0) {
          comprehensivePrompt = `${formData.prompt}\n\nAdditional Context:\n${additionalContext.join('\n')}`;
        }
      }

      // Get the current content - prioritize edited content (initialContent) first
      let currentContent = "";
      if (initialContent && initialContent.trim()) {
        currentContent = initialContent;
      } else if (translatedContent) {
        currentContent = translatedContent;
      } else if (finalContent) {
        currentContent = finalContent;
      } else if (parsedHTMLContent) {
        currentContent = parsedHTMLContent;
      } else if (streamingContent) {
        currentContent = streamingContent;
      }

      const draftData = {
        title: formData.title,
        prompt: comprehensivePrompt,
        language: formData.language?.value || formData.language,
        locationFocus: formData.locationFocus?.value || formData.locationFocus,
        role: formData.role,
        goal: formData.goal,
        constraints: formData.constraints,
        tags: uploadedData.tags,
        uid: uploadedData.uid,
        image: imageUrl, // Include the image URL in the draft
        generatedContent: currentContent
      };

      const response = await saveFreeFormContentDraft(draftData);
      
      
      // Store the saved content ID
      if (response && response.success && response.data && response.data.id) {
        setSavedContentId(response.data.id);
        setIsContentSaved(true);
        setHasUnsavedChanges(false);
        toast.success('Draft saved successfully!');
      } else {
        console.warn('Unexpected response structure:', response);
        toast.warning('Draft saved but no ID returned. Share functionality may not work.');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft: ' + error.message);
    }
  };

  const handleShare = () => {
    if (!savedContentId) {
      toast.warning('Please save the content first before sharing.');
      return;
    }

    // Create share URL
    const shareUrl = `${window.location.origin}/skills/free-form-content/${savedContentId}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        toast.success('Link copied to clipboard!');
      })
      .catch(() => {
        toast.error('Failed to copy link. Please copy manually: ' + shareUrl);
      });
  };

  const handleReset = () => {
    // Reset content state
    resetContent();
    setHasGeneratedContent(false);
    setIsContentSaved(false);
    setSavedContentId(null);
    setHasUnsavedChanges(false);
    
    // Reset form data
    setFormData({
      title: "",
      prompt: "",
      language: null,
      locationFocus: null,
      role: "",
      goal: "",
      constraints: "",
    });
    
    // Reset image
    setSelectedImage(null);
    setImageUrl("");
    setImageError("");
    
    // Reset edit mode state
    setInitialContent("");
    setEditedContent("");
    setIsEditMode(false);
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDiscardChanges = () => {
    if (window.confirm('Are you sure you want to discard your unsaved changes? This action cannot be undone.')) {
      // Reset to the last saved state
      setHasUnsavedChanges(false);
      // In a real app, you would reload the saved data here
    }
  };

  // Helper function to extract HTML content from JSON if needed
  const extractHtmlFromContent = (content) => {
    if (!content || typeof content !== 'string') return content;
    const trimmed = content.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed.html_content) return parsed.html_content;
        if (parsed.content) return parsed.content;
        if (parsed.content_type || parsed.metadata) return "";
      } catch (e) {
        const htmlMatch = trimmed.match(/"html_content"\s*:\s*"([\s\S]*?)"\s*(,|\})/);
        if (htmlMatch && htmlMatch[1]) {
          return htmlMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\/g, '');
        }
        if (trimmed.includes('content_type') || trimmed.includes('metadata')) return "";
      }
    }
    if (content.includes('content_type') && !content.includes('html_content') && !content.includes('<')) {
      return "";
    }
    return content;
  };

  const htmlToPlainText = (html) => {
    if (!html || typeof html !== 'string') return html;
    
    // If it doesn't contain HTML tags, return as-is
    if (!/<[^>]+>/.test(html)) {
      return html;
    }
    
    if (html.match(/<[^>]+>/g)?.every(tag => /^<br\s*\/?>$/i.test(tag))) {
      // Only contains <br> tags, safe to convert
      return html.replace(/<br\s*\/?>/gi, '\n');
    }
    
    // Complex HTML - return as-is
    return html;
  };

  // Handle edit mode toggle
  const handleToggleEditMode = () => {
    if (!isEditMode) {
      let currentContent = "";
      if (initialContent && initialContent.trim()) {
        currentContent = initialContent;
      } else if (translatedContent) {
        currentContent = translatedContent;
      } else if (finalContent) {
        currentContent = finalContent;
      } else if (parsedHTMLContent) {
        currentContent = parsedHTMLContent;
      } else if (streamingContent) {
        currentContent = streamingContent;
      }
      const cleanedContent = extractHtmlFromContent(currentContent);
      // Convert HTML back to plain text for editing (so line breaks and spaces are visible)
      const plainTextContent = htmlToPlainText(cleanedContent);
      setEditedContent(plainTextContent || "");
    }
    setIsEditMode(!isEditMode);
  };

  // Helper function to preserve line breaks in plain text only
  const preserveWhitespace = (content) => {
    if (!content || typeof content !== 'string') return content;
    
    // If content already contains HTML tags, return as-is (don't modify HTML)
    if (/<[^>]+>/.test(content)) {
      return content;
    }
    
    // Only for plain text: convert line breaks to <br> tags
    return content.replace(/\n/g, '<br>');
  };

  // Handle saving edited content
  const handleSaveEditedContent = () => {
    const cleanedContent = extractHtmlFromContent(editedContent);
     const preservedContent = preserveWhitespace(cleanedContent);
    
    setInitialContent(preservedContent);
    setTranslatedContent("");
    if (cleanedContent.trim()) {
      setHasGeneratedContent(true);
      setHasUnsavedChanges(true);
      toast.success("Content saved successfully!");
    } else {
      setHasGeneratedContent(false);
      toast.info("Editor cleared. You can add content manually.");
    }
    setIsEditMode(false);
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    let currentContent = "";
    if (initialContent && initialContent.trim()) {
      currentContent = initialContent;
    } else if (translatedContent) {
      currentContent = translatedContent;
    } else if (finalContent) {
      currentContent = finalContent;
    } else if (parsedHTMLContent) {
      currentContent = parsedHTMLContent;
    } else if (streamingContent) {
      currentContent = streamingContent;
    }
    setEditedContent(currentContent || "");
    setIsEditMode(false);
  };

  // Translation function
  const onTranslate = async () => {
    if (!initialContent && !finalContent && !streamingContent && !parsedHTMLContent) {
      toast.error("Please generate content first before translating");
      return;
    }

    // Check if language is selected
    if (!formData.language) {
      toast.error("Please select a target language for translation");
      return;
    }

    setIsTranslating(true);
    setTranslatedContent("");
    setTranslationStream("");
    
    // Store the original content before translation
    if (!originalContent) {
      setOriginalContent({
        finalContent: finalContent,
        streamingContent: streamingContent,
        parsedHTMLContent: parsedHTMLContent,
        formData: { ...formData }
      });
    }
    
    try {
       const contentToTranslate = initialContent && initialContent.trim() 
        ? initialContent 
        : (parsedHTMLContent || finalContent || streamingContent || "");
      
      // Prepare the JSON data to send for translation
      const jsonData = {
        content: contentToTranslate
      };

      // Call the backend translation endpoint
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication required. Please log in.");
      }

      const response = await fetch(`${axiosInstance.defaults.baseURL}/generates/translate_content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          request_type: "language_translate",
          additional_data: {
            json_data: jsonData,
            source_language: "English",
            target_language: formData.language?.label || formData.language || "English",
            target_region: formData.locationFocus?.label || formData.locationFocus || "Global"
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Translation API error: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedData = "";

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        accumulatedData += chunk;
        setTranslationStream(accumulatedData);
      }

      // Process the fragmented streaming data
      const translatedContent = processFragmentedStream(accumulatedData);
      
      if (translatedContent && translatedContent.trim()) {
        setInitialContent(translatedContent);
        // Also update translatedContent for display priority
        setTranslatedContent(translatedContent);
        setHasGeneratedContent(true);
        setHasUnsavedChanges(true);
        toast.success("Translation completed successfully! Your content has been replaced with the translated version.");
      } else {
        // Try to parse as JSON as fallback
        try {
          const cleanedData = cleanStreamingResponse(accumulatedData);
          const parsedTranslation = JSON.parse(cleanedData);
          
          if (parsedTranslation.content) {
            // Update initialContent as the single source of truth
            setInitialContent(parsedTranslation.content);
            // Also update translatedContent for display priority
            setTranslatedContent(parsedTranslation.content);
            setHasGeneratedContent(true);
            setHasUnsavedChanges(true);
            toast.success("Translation completed successfully! Your content has been replaced with the translated version.");
          } else {
            throw new Error("No content found in translation response");
          }
        } catch (parseError) {
          console.error("Error parsing translation response:", parseError);
          toast.warning("Translation completed but failed to parse response format");
        }
      }
      
    } catch (error) {
      console.error("Translation error:", error);
      toast.error("Translation failed: " + (error.message || "Unknown error"));
    } finally {
      setIsTranslating(false);
    }
  };

  // Helper function to clean streaming response (similar to blog implementation)
  const cleanStreamingResponse = (data) => {
    // Remove "data: " prefixes and newlines
    let cleaned = data.replace(/data:\s*/g, '').replace(/\n/g, '');
    
    // Try to find JSON-like content
    const jsonMatch = cleaned.match(/\{.*\}/);
    if (jsonMatch) {
      return jsonMatch[0];
    }
    
    return cleaned;
  };

  // Helper function to process complete HTML documents and extract body content with inline styles
  const processCompleteHTMLDocument = (htmlDocument) => {
    try {
      // Create a temporary div to parse the HTML document
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlDocument;
      
      // Extract the body content
      const bodyElement = tempDiv.querySelector('body');
      if (bodyElement) {
        // Get all the content from the body
        let bodyContent = bodyElement.innerHTML;
        
        // Extract CSS styles from the head section
        const headElement = tempDiv.querySelector('head');
        if (headElement) {
          const styleElement = headElement.querySelector('style');
          if (styleElement) {
            const cssText = styleElement.textContent;
            
            // Convert CSS to inline styles for the body content
            bodyContent = applyCSSToHTML(bodyContent, cssText);
          }
        }
        
        return bodyContent;
      }
      
      // Fallback: return the original content
      return htmlDocument;
    } catch (error) {
      console.error('Error processing complete HTML document:', error);
      return htmlDocument;
    }
  };

  // Helper function to apply CSS styles to HTML content
  const applyCSSToHTML = (htmlContent, cssText) => {
    try {
      // Create a temporary container to work with
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      
      // Parse CSS rules
      const cssRules = parseCSS(cssText);
      
      // Apply styles to matching elements
      cssRules.forEach(rule => {
        const elements = tempDiv.querySelectorAll(rule.selector);
        elements.forEach(element => {
          // Apply each CSS property
          Object.keys(rule.properties).forEach(property => {
            element.style[property] = rule.properties[property];
          });
        });
      });
      
      return tempDiv.innerHTML;
    } catch (error) {
      console.error('Error applying CSS to HTML:', error);
      return htmlContent;
    }
  };

  // Helper function to parse CSS text into rules
  const parseCSS = (cssText) => {
    const rules = [];
    const ruleRegex = /([^{]+)\{([^}]+)\}/g;
    let match;
    
    while ((match = ruleRegex.exec(cssText)) !== null) {
      const selector = match[1].trim();
      const propertiesText = match[2];
      const properties = {};
      
      // Parse properties
      const propertyRegex = /([^:]+):\s*([^;]+);?/g;
      let propMatch;
      
      while ((propMatch = propertyRegex.exec(propertiesText)) !== null) {
        const property = propMatch[1].trim();
        const value = propMatch[2].trim();
        properties[property] = value;
      }
      
      rules.push({ selector, properties });
    }
    
    return rules;
  };

  // Helper function to wrap translated text in original HTML structure
  const wrapTextInHTMLStructure = (translatedText, originalHTML) => {
    try {
      // Create a temporary DOM element to parse the original HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = originalHTML;
      
      // Find all text nodes and replace them with translated text
      const walker = document.createTreeWalker(
        tempDiv,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      let textNodes = [];
      let node;
      while (node = walker.nextNode()) {
        if (node.textContent.trim()) {
          textNodes.push(node);
        }
      }
      
      // If we have text nodes, replace them with translated content
      if (textNodes.length > 0) {
        // For now, just replace the first text node with the translated content
        // This is a simplified approach - in a real implementation, you'd want to
        // map the translated text more intelligently to different sections
        textNodes[0].textContent = translatedText;
        return tempDiv.innerHTML;
      }
      
      // Fallback: wrap the translated text in a div with the same styling
      const outerDiv = tempDiv.querySelector('div');
      if (outerDiv) {
        const style = outerDiv.getAttribute('style') || '';
        return `<div style="${style}">${translatedText}</div>`;
      }
      
      // Last fallback: return the translated text as is
      return translatedText;
    } catch (error) {
      console.error('Error wrapping text in HTML structure:', error);
      return translatedText;
    }
  };

  // Helper function to fix malformed HTML with missing spaces in attributes
  const fixMalformedHTML = (html) => {
    try {
      
      let fixed = html;
      
      // Fix specific malformed patterns with precise regex
      // Fix <divstyle=" -> <div style="
      fixed = fixed.replace(/<divstyle="/g, '<div style="');
      
      // Fix <h1style=" -> <h1 style="
      fixed = fixed.replace(/<h1style="/g, '<h1 style="');
      
      // Fix <h2style=" -> <h2 style="
      fixed = fixed.replace(/<h2style="/g, '<h2 style="');
      
      // Fix <pstyle=" -> <p style="
      fixed = fixed.replace(/<pstyle="/g, '<p style="');
      
      // Fix any other tag+style combinations
      fixed = fixed.replace(/<([a-zA-Z]+)style="/g, '<$1 style="');
      
      // Fix missing spaces between CSS properties in style attributes
      fixed = fixed.replace(/style="([^"]*)"/g, (match, styleContent) => {
        // Add spaces before CSS properties that are missing them
        const fixedStyle = styleContent
          .replace(/([a-zA-Z-]+):/g, ' $1:') // Add space before CSS property names
          .replace(/([a-zA-Z-]+):\s*([^;]+);/g, '$1: $2;') // Ensure space after colon
          .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
          .trim();
        return `style="${fixedStyle}"`;
      });
      
      
      return fixed;
    } catch (error) {
      console.warn('Error fixing malformed HTML:', error);
      return html;
    }
  };

  // Helper function to process fragmented streaming data using the same approach as content generation
  const processFragmentedStream = (data) => {
    // Use the same parsing approach as the free-form content generation
    const cleaned = data.replace(/data:\s*/g, '').replace(/\n/g, '');
    
    try {
      // First try to find complete JSON objects (same as content generation)
      const jsonObjects = extractCompleteJsonObjects(cleaned);
      for (const jsonObj of jsonObjects) {
        try {
          const parsed = JSON.parse(jsonObj.json);
          
          // Extract HTML content (same priority as content generation)
          if (parsed.html_content) {
            return fixMalformedHTML(parsed.html_content);
          } else if (parsed.content) {
            return fixMalformedHTML(parsed.content);
          }
        } catch (parseError) {
          console.warn('Failed to parse JSON object:', parseError);
          continue;
        }
      }
      
      // If no valid JSON objects found, try direct extraction (same as content generation)
      const directContent = extractHtmlContentDirectly(cleaned);
      if (directContent) {
        return fixMalformedHTML(directContent);
      }
      
      // Fallback: return the cleaned data
      return fixMalformedHTML(cleaned);
    } catch (error) {
      console.error('Error processing streaming data:', error);
      return fixMalformedHTML(cleaned);
    }
  };

  // Helper function to extract complete JSON objects from fragmented data (copied from service)
  const extractCompleteJsonObjects = (data) => {
    const jsonObjects = [];
    let braceCount = 0;
    let startIndex = -1;
    let currentIndex = 0;
    
    while (currentIndex < data.length) {
      const char = data[currentIndex];
      
      if (char === '{') {
        if (braceCount === 0) {
          startIndex = currentIndex;
        }
        braceCount++;
      } else if (char === '}') {
        braceCount--;
        if (braceCount === 0 && startIndex !== -1) {
          // Found a complete JSON object
          const jsonString = data.substring(startIndex, currentIndex + 1);
          
          // Validate that this looks like JSON
          if (isValidJsonStructure(jsonString)) {
            jsonObjects.push({
              json: jsonString,
              startIndex: startIndex,
              endIndex: currentIndex
            });
          }
          
          startIndex = -1;
        }
      }
      
      currentIndex++;
    }
    
    return jsonObjects;
  };

  // Helper function to validate JSON structure (copied from service)
  const isValidJsonStructure = (jsonString) => {
    const trimmed = jsonString.trim();
    
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
      return false;
    }
    
    if (trimmed.length < 3) {
      return false;
    }
    
    const hasQuotes = /"[^"]*":/.test(trimmed);
    const hasColon = trimmed.includes(':');
    
    return hasQuotes && hasColon;
  };

  // Helper function to extract HTML content directly (copied from service)
  const extractHtmlContentDirectly = (data) => {
    try {
      // Look for html_content field first (same priority as content generation)
      let htmlMatch = data.match(/"html_content"\s*:\s*"([^"]+)"/);
      if (!htmlMatch) {
        // Try without quotes around the key (malformed JSON)
        htmlMatch = data.match(/html_content\s*:\s*"([^"]+)"/);
      }
      if (!htmlMatch) {
        // Try with fragmented key
        htmlMatch = data.match(/html\s*_content\s*:\s*"([^"]+)"/);
      }
      
      if (htmlMatch) {
        const content = htmlMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
        return content;
      }
      
      // Look for content field (also handle unquoted keys)
      let contentMatch = data.match(/"content"\s*:\s*"([^"]+)"/);
      if (!contentMatch) {
        // Try without quotes around the key (malformed JSON)
        contentMatch = data.match(/content\s*:\s*"([^"]+)"/);
      }
      
      if (contentMatch) {
        const content = contentMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
        return content;
      }
      
      // Look for HTML-like content between quotes that contains HTML tags
      const htmlLikeMatch = data.match(/"[^"]*<[^"]*>[^"]*"/);
      if (htmlLikeMatch) {
        const content = htmlLikeMatch[0].replace(/^"/, '').replace(/"$/, '');
        return content;
      }
      return null;
    } catch (error) {
      console.warn('Error extracting HTML content directly:', error);
      return null;
    }
  };

  return (
    <div>
      <ToastContainer />
      <Breadcrumb />

      <div className="create-header">
        <h2>Create New Free Form Content</h2>
        <div className="header-actions">
          {/* Show Save as Draft button only after content is generated */}
          {hasGeneratedContent && (
            <Button 
              variant="outline-danger" 
              size="sm" 
              className="me-2" 
              onClick={handleSaveAsDraft}
              disabled={isGenerating}
            >
              {isContentSaved 
                ? (hasUnsavedChanges ? 'Update Draft' : 'Saved as Draft') 
                : 'Save as Draft'
              }
            </Button>
          )}
          
          {/* Show Discard Changes button when there are unsaved changes */}
          {hasUnsavedChanges && isContentSaved && (
            <Button 
              variant="outline-warning" 
              size="sm"
              className="me-2"
              onClick={handleDiscardChanges}
              disabled={isGenerating}
            >
              Discard Changes
            </Button>
          )}

          {/* Show Share button only after content is saved */}
          {isContentSaved && savedContentId && (
            <Button 
              variant="outline-danger" 
              size="sm"
              className="me-2"
              onClick={handleShare}
            >
              Share
            </Button>
          )}
          
          {/* Edit button - always visible to allow manual content entry, even without generated content */}
          <Button
            variant={isEditMode ? "warning" : "outline-primary"}
            size="sm"
            className="me-2"
            onClick={handleToggleEditMode}
            disabled={isGenerating || isTranslating}
          >
            {isEditMode ? "Cancel Edit" : "Edit Content"}
          </Button>
          
          {/* View button - only visible after saving */}
          {isContentSaved && savedContentId && (
            <Button 
              variant="outline-info" 
              size="sm"
              className="me-2"
              onClick={() => navigate(`/skills/free-form-content/view/${savedContentId}`)}
            >
              View
            </Button>
          )}
          
          {/* Reset button - always visible but disabled during generation */}
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={handleReset}
            disabled={isGenerating}
          >
            Reset
          </Button>
        </div>
      </div>

      <Row>
        {/* Left: Input Form */}
        <Col md={4}>
          {/* Content configuration form panel */}
          <Card className="create-form-panel">
            <Card.Body>
              {/* Content Status Indicator */}
              <div className="mb-3 content-status-indicator">
                {hasGeneratedContent && (
                  <Alert variant="success" className="py-2 mb-3">
                    <strong>✓ Content Generated</strong>
                    {isContentSaved && (
                      <span className="ms-2">• <strong>✓ Saved as Draft</strong></span>
                    )}
                    {hasUnsavedChanges && isContentSaved && (
                      <span className="ms-2">• <strong>⚠️ Unsaved Changes</strong></span>
                    )}
                  </Alert>
                )}
              </div>
              
              {/* Content Configuration Section */}
              <div className="mb-4">
                <h6 className="section-title mb-3">Provide Context</h6>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter a title for your content..."
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={6}
                      placeholder="Enter your prompt or question here..."
                      value={formData.prompt}
                      onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                      required
                    />
                  </Form.Group>

                  {/* Image Upload Section */}
                  <Form.Group className="mb-3">
                    <Form.Label>Expected Structure Image (Optional)</Form.Label>
                    <div className="image-upload-container">
                      {!imageUrl ? (
                        <div 
                          className="image-upload-area"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            style={{ display: 'none' }}
                          />
                          <Upload size={24} className="upload-icon" />
                          <p className="upload-text">Click to upload image</p>
                          <small className="text-muted">
                            Supported: JPEG, PNG (Max 10MB)
                          </small>
                        </div>
                      ) : (
                        <div className="image-preview-container">
                          <img 
                            src={imageUrl} 
                            alt="Content preview" 
                            className="image-preview"
                          />
                          <div className="image-actions">
                            <button
                              type="button"
                              className="replace-image-btn"
                              onClick={handleReplaceImage}
                              title="Replace image"
                            >
                              <Upload size={14} />
                            </button>
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={removeImage}
                              title="Remove image"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {imageError && (
                      <Alert variant="danger" className="mt-2 py-2">
                        {imageError}
                      </Alert>
                    )}
                    {isUploadingImage && (
                      <div className="text-center mt-2">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Uploading...</span>
                        </div>
                        <span className="ms-2">Uploading image...</span>
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Role</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter role"
                      value={formData.role || ""}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Goal</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter goal"
                      value={formData.goal || ""}
                      onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Constraints</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter constraints"
                      value={formData.constraints || ""}
                      onChange={(e) => setFormData({ ...formData, constraints: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Language <span className="text-danger">*</span> <span className="text-muted">(Required)</span></Form.Label>
                    <Select
                      options={languageOptions}
                      value={formData.language}
                      onChange={(selected) => setFormData({ ...formData, language: selected })}
                      isClearable
                      isSearchable
                      placeholder="Type to search language..."
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Location Focus <span className="text-danger">*</span> <span className="text-muted">(Required)</span></Form.Label>
                    <AsyncSelect
                      cacheOptions
                      loadOptions={loadLocationOptions}
                      value={formData.locationFocus}
                      onChange={(selected) => setFormData({ ...formData, locationFocus: selected })}
                      isClearable
                      isSearchable
                      placeholder="Type to search location..."
                      noOptionsMessage={({ inputValue }) =>
                        !inputValue
                          ? "Type to search..."
                          : "No locations found for that query"
                      }
                      loadingMessage={() => "Searching locations..."}
                    />
                  </Form.Group>

                  <div className="form-actions">
                    <Button 
                      variant="outline-danger" 
                      onClick={handleBack}
                      className="cancel-btn"
                      type="button"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="generate-btn"
                      variant="danger"
                      disabled={isGenerating}
                    >
                      {isGenerating ? "Generating..." : "Generate"}
                    </Button>
                  </div>
                  
                  {/* Translate button - styled like blog implementation */}
                  <button
                    type="button"
                    className="optimize-button mt-2"
                    onClick={onTranslate}
                    disabled={isTranslating || (!finalContent && !streamingContent) || !formData.language}
                  >
                    {isTranslating ? 'Translating...' : 'Translate'}
                  </button>
                  
                  {/* Translation help text */}
                  <div className="mt-2 text-muted" style={{ fontSize: '12px' }}>
                    {!formData.language && (finalContent || streamingContent) && (
                      <span>Select a target language to enable translation</span>
                    )}
                    {formData.language && (!finalContent && !streamingContent) && (
                      <span>Generate content first, then translate</span>
                    )}
                    {formData.language && (finalContent || streamingContent) && (
                      <span>Ready to translate to {formData.language.label || formData.language}</span>
                    )}
                  </div>
                  
                  {/* Help text based on current state */}
                  <div className="mt-3 help-text">
                    {!hasGeneratedContent && (
                      <div className="text-muted">
                        Fill in the form and click Generate to create your content.
                      </div>
                    )}
                    {hasGeneratedContent && !isContentSaved && (
                      <div className="text-success">
                        Content generated! Click "Save as Draft" to save your work.
                      </div>
                    )}
                    {isContentSaved && !hasUnsavedChanges && (
                      <div className="text-success">
                        Content saved! You can now share it with others.
                      </div>
                    )}
                    {isContentSaved && hasUnsavedChanges && (
                      <div className="text-warning">
                        You have unsaved changes. Click "Update Draft" to save your modifications.
                      </div>
                    )}
                  </div>
                </Form>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right: Streaming and Rendered Output */}
        <Col md={8} className="border-start">
          {/* Preview/Tab Buttons */}
          {!isEditMode && (
            <div className="d-flex gap-4 align-items-center mb-3">
              <h5 style={{ fontWeight: "500", fontSize: "16px" }}>
                Generated Content
              </h5>
              {(hasGeneratedContent || (metafields && metafields.length > 0)) && isContentSaved && (
                <div className="device-icons d-flex border border-black rounded justify-content-between align-items-center">
                  <h6 className="mt-2 mx-1">Preview</h6>
                  <div className="d-flex justify-content-center align-items-center">
                    <button 
                      className={`btn ${viewMode === "desktop" ? "active" : ""}`}
                      onClick={() => {
                        setViewMode("desktop");
                        handlePreview("desktop");
                      }}
                      style={{
                        border: viewMode === "desktop" ? "1px solid #E36250" : "1px solid #ccc",
                        backgroundColor: viewMode === "desktop" ? "#E36250" : "transparent",
                        color: viewMode === "desktop" ? "white" : "#333",
                        padding: "4px 8px",
                        margin: "0 2px",
                        borderRadius: "4px"
                      }}
                    >
                      <BiLaptop size={16} />
                    </button>
                    <span style={{ margin: "0 4px" }}>|</span>
                    <button 
                      className={`btn ${viewMode === "mobile" ? "active" : ""}`}
                      onClick={() => {
                        setViewMode("mobile");
                        handlePreview("mobile");
                      }}
                      style={{
                        border: viewMode === "mobile" ? "1px solid #E36250" : "1px solid #ccc",
                        backgroundColor: viewMode === "mobile" ? "#E36250" : "transparent",
                        color: viewMode === "mobile" ? "white" : "#333",
                        padding: "4px 8px",
                        margin: "0 2px",
                        borderRadius: "4px"
                      }}
                    >
                      <BiMobile size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Edit Mode - Show HTML Editor */}
          {isEditMode ? (
            <Card className="create-form-panel">
              <Card.Body>
                <div className="mb-3">
                  <h6 className="mb-2">Edit Content</h6>
                  <p className="text-muted small mb-3">
                    You can add content manually or edit existing content. Type your HTML or plain text content in the editor below and click "Save Changes" to apply. You can start with an empty editor and add content from scratch.
                  </p>
                  <Form.Control
                    as="textarea"
                    rows={20}
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    style={{ 
                      fontFamily: "monospace", 
                      fontSize: "13px",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word"
                    }}
                    placeholder="Enter your HTML content here. You can add content manually even if nothing has been generated yet..."
                  />
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    onClick={handleSaveEditedContent}
                    title="Save your content (can be empty to start fresh)"
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <StreamingContentRenderer
              title={formData.title ? formData.title : "Generated Content"}
              isStreaming={isStreaming || isTranslating}
              isGenerating={isGenerating}
              isTranslating={isTranslating}
              hasFirstChunk={hasFirstChunk || (isTranslating && translationStream.length > 0)}
              isStreamingDone={isStreamingDone && !isTranslating}
              streamingContent={isTranslating ? translationStream : streamingContent}
              // Prioritize: translatedContent > initialContent (manually edited) > other content
              parsedHTMLContent={
                isTranslating 
                  ? (initialContent && initialContent.trim() ? initialContent : parsedHTMLContent)
                  : (translatedContent && translatedContent.trim()
                      ? translatedContent
                      : (initialContent && initialContent.trim() 
                          ? initialContent 
                          : parsedHTMLContent))
              }
              finalContent={
                isTranslating
                  ? (initialContent && initialContent.trim() ? initialContent : finalContent)
                  : (translatedContent && translatedContent.trim()
                      ? translatedContent
                      : (initialContent && initialContent.trim()
                          ? initialContent
                          : finalContent))
              }
              initialContent={initialContent}
              noContentMessage="No content generated yet. Fill in the form and click Generate to see results."
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CreateFreeFormContent;
